/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import { DEFAULT_INVALID_VALUE, FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import DATE_FORMATS from '../../../utilities/DateTimeFormats';
import DataTable from '../../data-table/DataTable';
import { convertLoadParamsToQueryParams } from '../../data-table/Loaders';
import { useReportService, extractReportOptions } from '../../../services/ReportService';
import { useReportDialog } from '../../page-framework/report-dialog/ReportDialog';
import { MODES } from '../../page-framework/report-dialog/ReportOptions';
import { INTERVAL_LABELS } from '../../page-framework/report-dialog/RecurrenceOptions';
import { useReportActions } from './ReportActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { useRefreshTrigger } from '../../data-table/Triggers';

function getColumns(onEdit) {
    return [
        {
            mapping: 'name',
            title: t`Name`,
            width: 225,
            headerTooltip: `Scheduled report name`,
            sortable: true,
            renderer: _.partial(reportLinkRenderer, onEdit),
        },
        {
            title: t`Owner`,
            mapping: 'user.userName',
            headerTooltip: t`Scheduled report owner`,
            sortable: true,
        },
        {
            title: t`Recipients`,
            mapping: 'email.recipients',
            formatter: (row) => (row.length ? row.join(', ') : DEFAULT_INVALID_VALUE),
            sortable: false,
        },
        {
            title: t`Status`,
            mapping: 'disabled',
            formatter: (value) => i18n._(value === true ? t`Disabled` : t`Enabled`),
            headerTooltip: t`Scheduled report status. Disabled reports will not run`,
            sortable: true,
        },
        {
            title: t`Recurrence`,
            mapping: 'recurrence.interval',
            formatter: (value) => i18n._(INTERVAL_LABELS[value]),
            headerTooltip: t`Scheduled report run interval`,
            sortable: true,
        },
        {
            title: t`Last Success`,
            mapping: 'status.lastSuccess',
            formatter: {
                type: FORMATTER_TYPES.DATETIME,
                toFormat: DATE_FORMATS.DATETIME_MIN_12H,
            },
            headerTooltip: t`The last time each report was successful`,
            sortable: true,
        },
        {
            title: t`Last Error`,
            mapping: 'status.lastFailure',
            formatter: {
                type: FORMATTER_TYPES.DATETIME,
                toFormat: DATE_FORMATS.DATETIME_MIN_12H,
            },
            headerTooltip: t`The last time each report failed to run`,
            sortable: true,
        },
        {
            title: t`Last Error Message`,
            mapping: 'status.error',
            headerTooltip: t`The last error message for each report that failed to run`,
            flex: 1,
            sortable: true,
        },
    ];
}

function convertReportsToDataTableRows(response) {
    return {
        rows: response.data,
        length: response.count,
    };
}

function ReportLink({ onClick, report }) {
    function handleClick(event) {
        event.preventDefault();
        event.stopPropagation();
        onClick(extractReportOptions(report));
    }

    return (
        <a href="#" onClick={handleClick}>
            {report?.name}
        </a>
    );
}

function reportLinkRenderer(onClick, row) {
    return <ReportLink onClick={onClick} report={row} />;
}

export default function ReportsList() {
    const reportService = useReportService();
    const reportActionsContext = useReportActions();
    const [reportDialog, showReportDialog] = useReportDialog();
    const [tableState, setTableState] = useState(undefined);
    const tableSelectionRef = useRef();
    const { triggerRef, trigger: reloader } = useRefreshTrigger();

    // Used by the data table to load data.
    const loader = useCallback(
        (params) => {
            const queryParams = {
                all: reportActionsContext.showAll,
                ...convertLoadParamsToQueryParams(params),
            };

            return reportService.getReports(queryParams).then(convertReportsToDataTableRows);
        },
        [reportService, reportActionsContext.showAll]
    );

    const columns = useMemo(() => {
        return getColumns(_.partial(showReportDialog, MODES.SCHEDULE, reloader));
    }, [showReportDialog, reloader]);

    const getParams = useCallback(() => {
        if (tableState?.search) {
            return {
                all: reportActionsContext.showAll,
                search: tableState?.search,
            };
        }

        return {
            all: reportActionsContext.showAll,
        };
    }, [tableState, reportActionsContext.showAll]);

    const handleEnable = useCallback(() => {
        reportService.enableReports(getParams(), tableSelectionRef.current).then(reloader);
    }, [reportService, getParams, reloader]);

    const handleDisable = useCallback(() => {
        reportService.disableReports(getParams(), tableSelectionRef.current).then(reloader);
    }, [reportService, getParams, reloader]);

    const handleDelete = useCallback(() => {
        reportService.deleteReports(getParams(), tableSelectionRef.current).then(reloader);
    }, [reportService, getParams, reloader]);

    reportActionsContext.setOnEnable(handleEnable);
    reportActionsContext.setOnDisable(handleDisable);
    reportActionsContext.setOnDelete(handleDelete);

    function handleTableSelectionChange(tableSelection) {
        tableSelectionRef.current = tableSelection;
        const disableActions = !tableSelection || (_.isEmpty(tableSelection.selection) && !tableSelection.inverted);
        reportActionsContext.setActionsDisabled(disableActions);
    }

    return (
        <>
            <DataTable
                tableId="Reports List"
                loader={loader}
                triggerRef={triggerRef}
                columns={columns}
                visibleRows={0}
                showSelectionColumn
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
                onStateChanged={setTableState}
            />
            {reportDialog}
        </>
    );
}

ReportsList.propTypes = panelPropTypes;

ReportsList.defaultProps = {
    ...panelPropDefaults,
    title: t`Scheduled Reports`,
};
